import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { showError } from '@/lib/swal';

/**
 * SumUp Callback Page
 * 
 * This page handles the redirect from SumUp after payment completion.
 * It processes the payment callback to verify the payment and update the invoice.
 */
export default function SumUpCallbackPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      if (!invoiceId) {
        setStatus('error');
        setErrorMessage('Invalid invoice ID');
        return;
      }

      // Get checkout_id from URL params (SumUp may pass this in query string)
      // SumUp may use different parameter names: checkout_id, id, checkoutId, etc.
      const checkoutId = searchParams.get('checkout_id') || 
                        searchParams.get('id') || 
                        searchParams.get('checkoutId') ||
                        searchParams.get('checkout');
      
      if (!checkoutId) {
        // If no checkout_id in URL, try to get it from localStorage (set before redirect)
        // This is a fallback in case SumUp doesn't pass the checkout_id in the URL
        const storedCheckoutId = localStorage.getItem(`sumup_checkout_${invoiceId}`);
        if (storedCheckoutId) {
          console.log('Using stored checkout ID from localStorage', { invoiceId, checkoutId: storedCheckoutId });
          localStorage.removeItem(`sumup_checkout_${invoiceId}`);
          await processPaymentCallback(invoiceId, storedCheckoutId);
        } else {
          console.error('Checkout ID not found in URL params or localStorage', {
            invoiceId,
            urlParams: Object.fromEntries(searchParams.entries()),
          });
          setStatus('error');
          setErrorMessage('Checkout ID not found. Please contact support if you completed the payment.');
        }
        return;
      }

      console.log('Processing SumUp callback', { invoiceId, checkoutId });

      await processPaymentCallback(invoiceId, checkoutId);
    };

    processCallback();
    
    // Cleanup: Clear any pending retry timeouts when component unmounts
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [invoiceId, searchParams, navigate]);

  const processPaymentCallback = async (invoiceId: string, checkoutId: string, retryCount = 0) => {
    const maxRetries = 5; // Maximum number of retries for PENDING status
    
    try {
      // Call the backend function to process the SumUp callback
      const handleCallback = httpsCallable(functions, 'handleSumUpCallback');
      const result = await handleCallback({
        invoiceId,
        checkoutId,
      });

      const data = result.data as {
        success: boolean;
        status: string;
        amount?: number;
        newStatus?: string;
        transactionId?: string;
        transactionCode?: string;
        error?: string;
        retryAfter?: number;
      };

      if (data.success && data.status === 'PAID') {
        // Payment successful - verified on server
        setStatus('success');
        
        // Redirect to success page after a short delay
        setTimeout(() => {
          const successParams = new URLSearchParams({
            invoice_id: invoiceId,
            checkout_id: checkoutId,
            ...(data.transactionId && { transaction_id: data.transactionId }),
            ...(data.transactionCode && { transaction_code: data.transactionCode }),
          });
          navigate(`/payment/success?${successParams.toString()}`);
        }, 2000);
      } else if (data.status === 'FAILED') {
        // Payment failed - verified on server
        setStatus('error');
        setErrorMessage(data.error || 'Payment failed. Please try again.');
      } else if (data.status === 'PENDING') {
        // Payment still pending - implement retry logic
        if (retryCount < maxRetries) {
          const retryAfter = data.retryAfter || 30; // Default 30 seconds
          
          setStatus('processing');
          setErrorMessage(`Payment is still being processed. Retrying verification in ${retryAfter} seconds... (Attempt ${retryCount + 1}/${maxRetries})`);
          
          // Retry verification after delay
          retryTimeoutRef.current = setTimeout(async () => {
            console.log('Retrying payment verification for PENDING status', { 
              invoiceId, 
              checkoutId, 
              retryCount: retryCount + 1,
              maxRetries,
            });
            await processPaymentCallback(invoiceId, checkoutId, retryCount + 1);
          }, retryAfter * 1000);
        } else {
          // Max retries reached
          setStatus('error');
          setErrorMessage('Payment is taking longer than expected to process. Please check back in a few minutes or contact support if you completed the payment.');
        }
      } else {
        // Unknown status
        setStatus('error');
        setErrorMessage(data.error || 'Payment status unknown. Please contact support if you completed the payment.');
      }
    } catch (error: any) {
      console.error('Error processing SumUp callback:', error);
      setStatus('error');
      
      // Show more specific error messages
      if (error.code === 'functions/not-found') {
        setErrorMessage('Invoice not found. Please verify your payment link.');
      } else if (error.code === 'functions/failed-precondition') {
        setErrorMessage('Payment configuration error. Please contact support.');
      } else {
        setErrorMessage(error.message || 'Failed to process payment. Please contact support if you completed the payment.');
      }
      
      // Show error alert
      showError(error.message || 'Failed to process payment callback');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-4">
            {status === 'processing' && (
              <>
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
                <CardTitle className="text-2xl">Processing Payment</CardTitle>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <CardTitle className="text-2xl text-green-900">Payment Successful!</CardTitle>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <CardTitle className="text-2xl text-red-900">Payment Error</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-6 text-center">
            {status === 'processing' && (
              <>
                <p className="text-muted-foreground">
                  Please wait while we verify your payment...
                </p>
                <div className="flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <p className="text-green-900 mb-2">
                    <strong>Your payment has been processed successfully.</strong>
                  </p>
                  <p className="text-green-800 text-sm">
                    Your invoice has been updated. Redirecting to success page...
                  </p>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <p className="text-red-900 mb-2">
                    <strong>{errorMessage || 'An error occurred processing your payment.'}</strong>
                  </p>
                  {invoiceId && (
                    <p className="text-red-800 text-sm mt-2">
                      Invoice ID: <span className="font-mono text-xs">{invoiceId}</span>
                    </p>
                  )}
                </div>
                <div className="space-y-3 pt-4">
                  {invoiceId && (
                    <Button
                      onClick={() => navigate(`/payment/${invoiceId}`)}
                      className="w-full"
                      size="lg"
                      variant="outline"
                    >
                      Return to Payment Page
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full"
                    size="lg"
                  >
                    Return to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
